/**
 * 私募产品管理API服务
 * 封装所有API接口调用
 */
import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';

const baseUrl = getApiBaseUrl();
const apiBase = `${baseUrl}/api/private-fund`;

/**
 * 创建axios实例
 */
const api = axios.create({
  baseURL: apiBase,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * 请求拦截器
 */
api.interceptors.request.use(
  (config) => {
    // 添加JWT token到请求头
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * 响应拦截器
 */
api.interceptors.response.use(
  (response) => {
    // 直接返回 response.data，因为后端返回的格式是 { success: true, data: ..., ... }
    // axios 会自动将响应体解析到 response.data 中
    return response.data;
  },
  (error) => {
    // 处理错误响应
    if (error.response) {
      // 处理401错误（未认证）
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(new Error('未登录，请先登录'));
      }
      // 服务器返回了错误状态码
      const errorData = error.response.data;
      const message = errorData?.message || errorData?.error || error.message || '请求失败';
      return Promise.reject(new Error(message));
    } else if (error.request) {
      // 请求已发出但没有收到响应
      return Promise.reject(new Error('网络错误，请检查服务器是否运行'));
    } else {
      // 其他错误
      return Promise.reject(new Error(error.message || '请求失败'));
    }
  }
);

/**
 * 产品管理API
 */
export const productApi = {
  /**
   * 获取产品列表
   */
  getProducts: (params = {}) => {
    return api.get('/products', { params });
  },

  /**
   * 获取产品详情
   */
  getProductById: (id) => {
    return api.get(`/products/${id}`);
  },

  /**
   * 创建产品
   */
  createProduct: (data) => {
    return api.post('/products', data);
  },

  /**
   * 更新产品
   */
  updateProduct: (id, data) => {
    return api.put(`/products/${id}`, data);
  },

  /**
   * 删除产品
   */
  deleteProduct: (id) => {
    return api.delete(`/products/${id}`);
  }
};

/**
 * 净值管理API
 */
export const netValueApi = {
  /**
   * 更新最新净值
   */
  updateNetValue: (productId, data) => {
    return api.put(`/products/${productId}/net-value`, data);
  },

  /**
   * 获取历史净值列表
   */
  getNetValueHistory: (productId, params = {}) => {
    return api.get(`/products/${productId}/net-value-history`, { params });
  },

  /**
   * 添加历史净值
   */
  addNetValueHistory: (productId, data) => {
    return api.post(`/products/${productId}/net-value-history`, data);
  },

  /**
   * 更新历史净值
   */
  updateNetValueHistory: (historyId, data) => {
    return api.put(`/net-value-history/${historyId}`, data);
  },

  /**
   * 删除历史净值
   */
  deleteNetValueHistory: (historyId) => {
    return api.delete(`/net-value-history/${historyId}`);
  },

  /**
   * 批量导入历史净值
   */
  batchImportNetValueHistory: (productId, data) => {
    return api.post(`/products/${productId}/net-value-history/batch-import`, data);
  }
};

/**
 * 赎回管理API
 */
export const redemptionApi = {
  /**
   * 记录赎回
   */
  createRedemption: (productId, data) => {
    return api.post(`/products/${productId}/redemption`, data);
  },

  /**
   * 获取赎回记录
   */
  getRedemption: (productId) => {
    return api.get(`/products/${productId}/redemption`);
  },

  /**
   * 设置已赎回产品的最终盈亏
   */
  setFinalProfitLoss: (productId, profitLoss) => {
    return api.put(`/products/${productId}/redemption`, { profitLoss });
  },

  /**
   * 更新赎回记录
   */
  updateRedemption: (redemptionId, data) => {
    return api.put(`/redemptions/${redemptionId}`, data);
  },

  /**
   * 删除赎回记录
   */
  deleteRedemption: (redemptionId) => {
    return api.delete(`/redemptions/${redemptionId}`);
  }
};

/**
 * 分红管理API
 */
export const dividendApi = {
  /**
   * 记录分红
   */
  createDividend: (productId, data) => {
    return api.post(`/products/${productId}/dividends`, data);
  },

  /**
   * 获取分红记录列表
   */
  getDividends: (productId) => {
    return api.get(`/products/${productId}/dividends`);
  },

  /**
   * 更新分红记录
   */
  updateDividend: (dividendId, data) => {
    return api.put(`/dividends/${dividendId}`, data);
  },

  /**
   * 删除分红记录
   */
  deleteDividend: (dividendId) => {
    return api.delete(`/dividends/${dividendId}`);
  }
};

/**
 * 业绩报酬管理API
 */
export const performanceFeeApi = {
  /**
   * 记录业绩报酬
   */
  createPerformanceFee: (productId, data) => {
    return api.post(`/products/${productId}/performance-fees`, data);
  },

  /**
   * 获取业绩报酬记录列表
   */
  getPerformanceFees: (productId) => {
    return api.get(`/products/${productId}/performance-fees`);
  },

  /**
   * 更新业绩报酬记录
   */
  updatePerformanceFee: (feeId, data) => {
    return api.put(`/performance-fees/${feeId}`, data);
  },

  /**
   * 删除业绩报酬记录
   */
  deletePerformanceFee: (feeId) => {
    return api.delete(`/performance-fees/${feeId}`);
  }
};

/**
 * 盈亏分析API
 */
export const profitLossApi = {
  /**
   * 获取产品盈亏分析
   */
  getProductProfitLoss: (productId) => {
    return api.get(`/products/${productId}/profit-loss`);
  },

  /**
   * 获取汇总盈亏分析
   */
  getSummaryProfitLoss: () => {
    return api.get('/profit-loss/summary');
  },

  /**
   * 获取个人盈亏分析
   */
  getInvestorProfitLoss: (investorId) => {
    return api.get(`/investors/${investorId}/profit-loss`);
  }
};

/**
 * 统计API
 */
export const statisticsApi = {
  /**
   * 获取周收益统计
   */
  getWeeklyReturn: (productId) => {
    return api.get(`/products/${productId}/statistics/weekly-return`);
  },

  /**
   * 获取历史收益趋势
   */
  getReturnTrend: (productId, params = {}) => {
    return api.get(`/products/${productId}/statistics/return-trend`, { params });
  },

  /**
   * 获取收益对比
   */
  getReturnComparison: (params = {}) => {
    return api.get('/statistics/return-comparison', { params });
  }
};

/**
 * 投资人员管理API
 */
export const investorApi = {
  /**
   * 获取投资人员列表
   */
  getInvestors: () => {
    return api.get('/investors');
  },

  /**
   * 获取投资人员详情
   */
  getInvestorById: (investorId) => {
    return api.get(`/investors/${investorId}`);
  },

  /**
   * 更新投资人员统计
   */
  updateInvestorStatistics: (investorId) => {
    return api.post(`/investors/${investorId}/update-statistics`);
  },

  /**
   * 更新所有投资人员统计
   */
  updateAllInvestorStatistics: () => {
    return api.post('/investors/update-all-statistics');
  }
};

/**
 * 用户管理API（仅管理员）
 */
export const userManagementApi = {
  /**
   * 获取所有用户
   */
  getAllUsers: () => {
    return api.get('/users');
  },

  /**
   * 创建用户
   */
  createUser: (data) => {
    return api.post('/users', data);
  },

  /**
   * 更新用户
   */
  updateUser: (id, data) => {
    return api.put(`/users/${id}`, data);
  },

  /**
   * 删除用户
   */
  deleteUser: (id) => {
    return api.delete(`/users/${id}`);
  }
};

const apiExports = {
  productApi,
  netValueApi,
  redemptionApi,
  dividendApi,
  performanceFeeApi,
  profitLossApi,
  statisticsApi,
  investorApi,
  userManagementApi
};

export default apiExports;